/**
 * Deal stage constants and mappings
 * Migrated from hubspot-deals frontend
 */

// All deal stages with HubSpot IDs
export const DEAL_STAGES = [
  { id: '', label: 'All Stages' },
  { id: 'appointmentscheduled', label: 'Quote Requested' },
  { id: '4383256790', label: 'Quote Ready' },
  { id: 'qualifiedtobuy', label: 'Quote Sent' },
  { id: '2136864980', label: 'Warehouse Drop Off' },
  { id: '597442256', label: 'Awaiting Shipping Label' },
  { id: '4367683815', label: 'Awaiting Collection' },
  { id: 'presentationscheduled', label: 'Shipping Arranged' },
  { id: '835164638', label: 'Arrived To Test' },
  { id: 'contractsent', label: 'Awaiting Payment' },
  { id: 'closedwon', label: 'Closed Traded' },
  { id: 'closedlost', label: 'Closed Lost' },
  { id: '589630139', label: 'Quote Chased' },
  { id: '589142218', label: 'Problem With Shipping' },
  { id: '588059587', label: 'Issue with Item' },
  { id: '609520315', label: 'Listing Service Offered' },
  { id: '853396936', label: 'Listing Service Listing' },
  { id: '638945017', label: 'Last Chance Offer' },
  { id: '901659893', label: 'More Info Needed' },
  { id: '1616888039', label: 'Duplicate Trade Request' },
  { id: '4962263243', label: 'Part Ex' },
  { id: '4311086309', label: 'Artifacts' }
]

// Stages where parsed items can be edited
export const EDITABLE_STAGES = [
  'appointmentscheduled',  // Quote Requested
  '4383256790',            // Quote Ready
  'qualifiedtobuy'         // Quote Sent
]

// Get stage label from ID
export function getStageName(stageId) {
  const stage = DEAL_STAGES.find(s => s.id === stageId)
  return stage ? stage.label : stageId
}

// Extract search term from parsed item line
// Handles both old format: "1. Citronic CMC16 - Mixing Console - Qty:1 - SP:£0 - BP:£0"
// and new format: "Fender Strat, excellent, qty 6"
// Returns just the product name (e.g. "Citronic CMC16" or "Fender Strat")
export function getSearchTerm(text) {
  if (!text) return ''

  // Remove leading number and period (e.g., "1. " or "12. ")
  let cleaned = text.replace(/^\d+\.\s*/, '')

  // Find the first " - " (space-dash-space) and take everything before it
  const dashIndex = cleaned.indexOf(' - ')
  if (dashIndex > 0) {
    cleaned = cleaned.substring(0, dashIndex)
  }

  // Strip trailing condition/qty from comma-separated format
  const knownConditions = ['excellent', 'good', 'fair', 'poor', 'new', 'mint', 'not working']
  let changed = true
  while (changed) {
    changed = false
    const commaIndex = cleaned.lastIndexOf(',')
    if (commaIndex > 0) {
      const after = cleaned.substring(commaIndex + 1).trim().toLowerCase()
      if (knownConditions.includes(after) || /^qty\s+\d+$/i.test(after)) {
        cleaned = cleaned.substring(0, commaIndex).trim()
        changed = true
      }
    }
  }

  return cleaned.trim()
}

// Generate search URLs for external marketplaces
export function getSearchUrls(title) {
  const searchTerm = encodeURIComponent(getSearchTerm(title))
  return {
    reverbSold: `https://reverb.com/uk/marketplace?query=${searchTerm}&show_only_sold=true`,
    reverbActive: `https://reverb.com/uk/marketplace?query=${searchTerm}`,
    ebayActive: `https://www.ebay.co.uk/sch/i.html?_nkw=${searchTerm}&_sacat=0&LH_BIN=1&_dmd=2&rt=nc`,
    ebaySold: `https://www.ebay.co.uk/sch/i.html?_nkw=${searchTerm}&_sacat=0&LH_Complete=1&LH_Sold=1&rt=nc&LH_BIN=1&_dmd=2&rt=nc`,
    google: `https://www.google.com/search?q=${searchTerm}`
  }
}

// Parse items from parsed_items text field
export function parseItems(parsedItemsText) {
  if (!parsedItemsText) return []
  return parsedItemsText.split('\n').filter(line => line.trim())
}

// Parse a single item line into structured data
// Handles both old format: "1. Korg Krome 88 - Digital Piano - Qty:1 - SP:£709 - BP:£351"
// and new format: "Fender Strat, excellent, qty 6"
export function parseItemLine(line) {
  if (!line) return null

  // Remove leading number and period
  let text = line.replace(/^\d+\.\s*/, '')

  // Extract SP and BP — handle both "SP:£109" and non-standard "SP-109" / "BP-109" formats
  const spMatch = text.match(/\bSP[-:.]?\s*£?([\d,.]+)/i)
  const bpMatch = text.match(/\bBP[-:.]?\s*£?([\d,.]+)/i)
  const qtyMatch = text.match(/\bQty[:\s]\s*(\d+)/i)
  const srcMatch = text.match(/\[src:(\d+)\]/i)
  const pxMatch = text.match(/\bPX:(Yes|No)/i)

  const sp = spMatch ? parseFloat(spMatch[1].replace(',', '')) : null
  const bp = bpMatch ? parseFloat(bpMatch[1].replace(',', '')) : null
  const qty = qtyMatch ? parseInt(qtyMatch[1]) : 1
  const sourceId = srcMatch ? srcMatch[1] : null
  const isPx = pxMatch ? pxMatch[1].toLowerCase() === 'yes' : false

  // Remove the metadata parts to get the description
  let description = text
    .replace(/\s*-?\s*\bSP[-:.]?\s*£?[\d,.]+/gi, '')
    .replace(/\s*-?\s*\bBP[-:.]?\s*£?[\d,.]+/gi, '')
    .replace(/\s*-\s*Qty[:\s]\s*\d+/gi, '')
    .replace(/,\s*qty\s+\d+/gi, '')
    .replace(/\s*\[src:\d+\]/gi, '')
    .replace(/\s*-?\s*\bPX:(Yes|No)/gi, '')
    .trim()

  // Extract condition — check dash-separated format first, then comma-separated
  const knownConditions = ['excellent', 'very good', 'good', 'fair', 'poor', 'new', 'mint', 'not working']
  let condition = null

  // Dash format: "Brand Model - Details - Very Good" (after SP/BP/Qty/PX stripped)
  const dashParts = description.split(' - ').map(p => p.trim()).filter(Boolean)
  if (dashParts.length >= 2) {
    const lastDash = dashParts[dashParts.length - 1].toLowerCase()
    if (knownConditions.includes(lastDash)) {
      condition = dashParts.pop().trim()
      description = dashParts.join(' - ')
    }
  }

  // Comma format fallback: "Fender Strat, excellent"
  if (!condition) {
    const descParts = description.split(',').map(p => p.trim())
    if (descParts.length >= 2) {
      const lastPart = descParts[descParts.length - 1].toLowerCase()
      if (knownConditions.includes(lastPart)) {
        condition = descParts.pop().trim()
        description = descParts.join(', ').trim()
      }
    }
  }

  // Split into name and details
  const parts = description.split(' - ')
  const name = parts[0] || description
  const details = parts.slice(1).join(' · ')

  return { name, details, condition, qty, sp, bp, sourceId, isPx, raw: line }
}
